import { useState } from 'react'

import { ThemeProvider } from '@mui/material'
import {
  DateTimePickerProps as MuiDateTimePickerProps,
  DateTimePicker as MuiDateTimePicker,
} from '@mui/x-date-pickers/DateTimePicker'
import classNames from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'

import '@mui/x-date-pickers/themeAugmentation'
import { theme } from './styles'
import { FieldLabel } from '@/Atomic/Atoms/FieldLabel'
import { Icon } from '@/Atomic/Atoms/Icon'

import '../FieldStates.scss'

type DateTimeValue = string | Date | Dayjs | null | undefined

interface DateTimePickerProps extends Omit<
  MuiDateTimePickerProps,
  'value' | 'defaultValue' | 'onChange'
> {
  name: string
  title?: string
  requirementState?: 'required' | 'optional'
  onChange?: (...event: unknown[]) => void
  className?: string
  clearable?: boolean
  value?: DateTimeValue
  defaultValue?: DateTimeValue
  error?: string
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  name,
  title,
  requirementState,
  value,
  defaultValue,
  onChange,
  className,
  clearable = true,
  disabled,
  slotProps: incomingSlotProps,
  error,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const isReadonlyField = Boolean(disabled)
  const isControlled = value !== undefined
  dayjs.extend(updateLocale)
  // Set week start to Monday for supported locales
  dayjs.updateLocale('en', {
    weekStart: 1,
  })
  dayjs.updateLocale('de', {
    weekStart: 1,
  })
  dayjs.updateLocale('pl', {
    weekStart: 1,
  })
  dayjs.updateLocale('fr', {
    weekStart: 1,
  })

  const openPicker = () => {
    if (disabled) return
    setIsOpen(true)
  }

  return (
    <div
      className={classNames(className, {
        'field--readonly': isReadonlyField,
      })}
    >
      <ThemeProvider theme={theme}>
        <FieldLabel
          name={name}
          label={title}
          requirementState={requirementState}
        />
        <MuiDateTimePicker
          name={name}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          {...(isControlled
            ? { value: value ? dayjs(value) : null }
            : { defaultValue: defaultValue ? dayjs(defaultValue) : undefined })}
          onChange={(date) => onChange?.(date ? date.toISOString() : null)}
          slotProps={{
            ...incomingSlotProps,
            field: {
              ...incomingSlotProps?.field,
              clearable: clearable && !disabled,
            },
            textField: {
              ...incomingSlotProps?.textField,
              onClick: openPicker,
              ...(isReadonlyField
                ? { error: false, helperText: undefined }
                : { error: error ? true : false }),
            },
            inputAdornment: {
              position: 'end',
              ...incomingSlotProps?.inputAdornment,
              onClick: openPicker,
            },
          }}
          disabled={disabled}
          {...props}
        />
        {error && (
          <div className="field-error" role="alert">
            <Icon icon="info-circle" size="sm" />
            <span>{error}</span>
          </div>
        )}
      </ThemeProvider>
    </div>
  )
}
